<script setup lang="ts">
import { computed } from 'vue';
import { computedAsync } from '@vueuse/core';

import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';

import { AppLinkButton, AppStars, AppTag } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import type { AlternativeHotel } from '../../../models';

import AccommodationAmenities from './AccommodationAmenities.vue';

interface AlternativeAccommodationCardContentProps {
  hotel: AlternativeHotel;
  ingredientIndex: number;
}

const props = defineProps<AlternativeAccommodationCardContentProps>();

const isHotelGuestRatingExperiment = computedAsync(
  () => isExperimentAsync('hotel_guest_rating'),
  false
);
interface AlternativeAccommodationCardContentEmits {
  (eventName: 'show-details'): void;
  (eventName: 'show-rooms'): void;
}

const emit = defineEmits<AlternativeAccommodationCardContentEmits>();

const hasMultipleRooms = computed<boolean>(
  () => props.hotel.roomsInfo.length > 1
);
</script>

<template>
  <div
    role="button"
    tabindex="0"
    class="alternative-accommodation-card-content"
    @click.stop="emit('show-details')"
    @keydown.enter.stop="emit('show-details')"
  >
    <div class="info-container">
      <span class="hotel-name">
        {{ props.hotel.hotelName }}
      </span>

      <span class="polygon-name">
        {{ props.hotel.polygonName }}
      </span>

      <div class="info-wrapper">
        <AppStars size="18" :amount="props.hotel.stars" />
        <CustomersRatingTag
          v-if="isHotelGuestRatingExperiment && props.hotel.guestRating"
          :guest-rating="props.hotel.guestRating"
        />
        <AppTag v-if="props.hotel.mealName" type="grey" size="s">
          <template #title>
            <span class="tag-text">
              {{ props.hotel.mealName }}
            </span>
          </template>
        </AppTag>
      </div>
      <AccommodationAmenities :amenities="props.hotel.amenities" />
    </div>

    <div class="rooms-container">
      <div
        v-for="element in props.hotel.roomsInfo"
        :key="element.label + element.description"
        class="row"
      >
        <div :class="['description', { 'single-room': !hasMultipleRooms }]">
          <span v-if="hasMultipleRooms" class="label">
            {{ element.label }}:
          </span>
          {{ element.description }}
        </div>
      </div>
      <AppLinkButton
        :text="$t('planner.edit_room')"
        size="small"
        @click="emit('show-rooms')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.alternative-accommodation-card-content {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-16) var(--spacing-12);
  gap: var(--spacing-12);
  justify-content: space-between;
  height: 100%;

  .info-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    .hotel-name {
      color: var(--color-black);
      @include font-label-l-bold;
      line-height: var(--line-heights-16);
      margin-bottom: var(--spacing-4);

      display: -webkit-box;
      max-width: var(--card-width);
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .polygon-name {
      @include font-label-s-light;
      color: var(--color-secondary-60);
    }

    .info-wrapper {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-8);

      .tag-text {
        @include font-label-s-bold;
      }
    }
  }

  .rooms-container {
    padding: var(--spacing-16);
    background: var(--color-secondary-97);
    border: 1px solid var(--color-secondary-93);
    border-radius: var(--border-radius-8);
    margin: auto 0 0;

    .row {
      display: flex;
      gap: var(--spacing-4);
      margin-bottom: var(--spacing-8);

      .description {
        @include font-label-s-light;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        width: 0;
        min-width: 100%;

        .label {
          @include font-label-s-bold;
          white-space: nowrap;
        }

        &.single-room {
          white-space: normal;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          line-clamp: 2;
          -webkit-line-clamp: 2;
          line-height: var(--line-heights-14);
        }
      }
    }
  }
}
</style>
